import { useQuery } from "@tanstack/react-query";
import { Activity, RefreshCw } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { MessageDrawer } from "@/components/messages/MessageDrawer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { canonicalApi } from "@/lib/api-canonical";
import {
  messagesApi,
  type MessageFilters,
  type MessageState,
} from "@/lib/api-messages";
import { usePaginationState } from "@/lib/pagination";
import { ingressApi } from "@/lib/api-ingress";

const STATES: MessageState[] = [
  "RAW",
  "CANONICAL",
  "ENRICHED",
  "PENDING_MAPPING",
  "COMPLETED",
  "FAILED",
];

const STATE_TONE: Record<MessageState, "active" | "draft" | "deprecated" | "danger" | "warning" | "neutral"> = {
  RAW: "draft",
  CANONICAL: "neutral",
  ENRICHED: "neutral",
  COMPLETED: "active",
  PENDING_MAPPING: "warning",
  FAILED: "danger",
};

export function MessagesPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = React.useState<MessageFilters>({});
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const { page, pageSize, setPage, setPageSize, reset } = usePaginationState();

  const query = useQuery({
    queryKey: ["messages", filters, page, pageSize],
    queryFn: () => messagesApi.list({ ...filters, page, page_size: pageSize }),
    refetchInterval: 10_000,
  });

  // Lookup: canonical types come from the canonical schemas registry.
  // We deduplicate by canonical_type (a single type can have several
  // versions) and show the highest version in the description.
  const canonicalQuery = useQuery({
    queryKey: ["canonical", "all"],
    queryFn: () => canonicalApi.list({ page_size: 100 }),
    select: (p) => p.items,
    staleTime: 60_000,
  });
  const canonicalOptions = React.useMemo<ComboboxOption[]>(() => {
    const rows = canonicalQuery.data ?? [];
    const highestVersionByType = new Map<string, number>();
    rows.forEach((r) => {
      const cur = highestVersionByType.get(r.canonical_type) ?? 0;
      if (r.version > cur) highestVersionByType.set(r.canonical_type, r.version);
    });
    return Array.from(highestVersionByType.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([type, v]) => ({
        value: type,
        label: type,
        description: `latest v${v}`,
      }));
  }, [canonicalQuery.data]);

  // Source: registered ingress sources are suggestions, but the user can
  // still type any free-form source (allowCustom=true) since unregistered
  // sources are also accepted by the receiver in dev. The Message.source
  // string is "<type>:<name>" for non-webhook ingress and "<name>" for
  // webhooks for legacy reasons — both shapes get suggested.
  const ingressQuery = useQuery({
    queryKey: ["ingress", "all-for-picker"],
    queryFn: () => ingressApi.list({ page_size: 100 }),
    select: (p) => p.items,
    staleTime: 60_000,
  });
  const sourceOptions = React.useMemo<ComboboxOption[]>(
    () =>
      (ingressQuery.data ?? []).map((s) => ({
        value: s.type === "webhook" ? s.name : `${s.type}:${s.name}`,
        label: s.name,
        description: s.type,
      })),
    [ingressQuery.data],
  );

  function update<K extends keyof MessageFilters>(key: K, value: MessageFilters[K] | "") {
    setFilters((f) => {
      const next = { ...f };
      if (value === "" || value === undefined) {
        delete next[key];
      } else {
        next[key] = value as MessageFilters[K];
      }
      return next;
    });
    reset();
  }

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  return (
    <PageContainer>
      <PageHeader
        title={t("messages.title")}
        description={t("messages.subtitle")}
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            <RefreshCw
              className={"h-3.5 w-3.5 " + (query.isFetching ? "animate-spin" : "")}
              strokeWidth={1.5}
            />
            Refresh
          </Button>
        }
      />
      <PageContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="space-y-1">
            <Label htmlFor="f_source">Source</Label>
            <Combobox
              id="f_source"
              options={sourceOptions}
              value={filters.source ?? null}
              onChange={(v) => update("source", v ?? "")}
              placeholder="any"
              allowCustom
              loading={ingressQuery.isLoading}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="f_ct">Canonical type</Label>
            <Combobox
              id="f_ct"
              options={canonicalOptions}
              value={filters.canonical_type ?? null}
              onChange={(v) => update("canonical_type", v ?? "")}
              placeholder="any"
              loading={canonicalQuery.isLoading}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="f_state">State</Label>
            <select
              id="f_state"
              value={filters.state ?? ""}
              onChange={(e) => update("state", e.target.value as MessageState | "")}
              className="w-full"
            >
              <option value="">All states</option>
              {STATES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>

        {items.length > 0 ? (
          <div className="rounded-lg overflow-hidden border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th>Received</Th>
                  <Th>Source</Th>
                  <Th>Format</Th>
                  <Th>Canonical</Th>
                  <Th>State</Th>
                  <Th>Message ID</Th>
                </Tr>
              </Thead>
              <Tbody>
                {items.map((m) => (
                  <Tr
                    key={m.message_id}
                    className="cursor-pointer"
                    onClick={() => setSelectedId(m.message_id)}
                  >
                    <Td className="text-muted-foreground text-xs whitespace-nowrap">
                      {new Date(m.received_at).toLocaleString()}
                    </Td>
                    <Td className="font-mono text-xs">{m.source}</Td>
                    <Td className="text-muted-foreground text-xs">{m.raw_format ?? "—"}</Td>
                    <Td className="font-mono text-xs">{m.canonical_type ?? "—"}</Td>
                    <Td>
                      <Badge tone={STATE_TONE[m.state]}>{m.state}</Badge>
                    </Td>
                    <Td className="font-mono text-xs text-muted-foreground truncate max-w-[12rem]">
                      {m.message_id}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </div>
        ) : query.isLoading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <EmptyState
            icon={Activity}
            title="No messages yet"
            description="Post a JSON payload to POST /api/webhooks/<source> with shape {canonical_type, payload} to see it land here."
          />
        )}
      </PageContent>

      <MessageDrawer messageId={selectedId} onOpenChange={(o) => !o && setSelectedId(null)} />
    </PageContainer>
  );
}
